import { useCallback, useEffect, useState } from "react";
import { adminGqlRequest } from "@/lib/graphql";

const ADMIN_ORDERS_QUERY = `
  query AdminOrders($input: AdminOrdersInput) {
    adminOrders(input: $input) {
      items {
        id
        externalOrderId
        sellerName
        sellerWorkspaceId
        marketplace
        status
        rawStatus
        orderedAt
        lastUpdatedAt
        itemCount
        gmv
        currency
        fulfillmentType
        slaStatus
        riskLevel
        issueCount
      }
      totalCount
      page
      pageSize
      totalPages
      kpi {
        todayCount
        todayCountChangePct
        pendingCount
        shippedTodayCount
        shippedTodayChangePct
        returnsOpen
        cancelled30d
        gmv30d
        gmv30dChangePct
        atRiskCount
        breachedCount
      }
    }
  }
`;

export interface AdminOrderItem {
  id: string;
  externalOrderId: string;
  sellerName: string;
  sellerWorkspaceId: string;
  marketplace: string;
  status: string;
  rawStatus: string | null;
  orderedAt: string;
  lastUpdatedAt: string | null;
  itemCount: number | null;
  gmv: number | null;
  currency: string | null;
  fulfillmentType: string | null;
  slaStatus: string;
  riskLevel: string;
  issueCount: number;
}

export interface AdminOrdersKpi {
  todayCount: number;
  todayCountChangePct: number;
  pendingCount: number;
  shippedTodayCount: number;
  shippedTodayChangePct: number;
  returnsOpen: number;
  cancelled30d: number;
  gmv30d: number;
  gmv30dChangePct: number;
  atRiskCount: number;
  breachedCount: number;
}

export interface AdminOrdersPayload {
  items: AdminOrderItem[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  kpi: AdminOrdersKpi;
}

export interface AdminOrdersFilters {
  search?: string;
  status?: string;
  provider?: string;
  slaStatus?: string;
  riskLevel?: string;
  sortBy?: string;
  sortDir?: "asc" | "desc";
  page?: number;
  pageSize?: number;
}

export function useAdminOrders(filters: AdminOrdersFilters = {}) {
  const [data, setData] = useState<AdminOrdersPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await adminGqlRequest<{ adminOrders: AdminOrdersPayload }>(
        ADMIN_ORDERS_QUERY,
        {
          input: {
            search: filters.search || undefined,
            status: filters.status || undefined,
            provider: filters.provider || undefined,
            slaStatus: filters.slaStatus || undefined,
            riskLevel: filters.riskLevel || undefined,
            sortBy: filters.sortBy || undefined,
            sortDir: filters.sortDir || undefined,
            page: filters.page ?? 1,
            pageSize: filters.pageSize ?? 15,
          },
        },
      );
      setData(result.adminOrders);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load orders");
    } finally {
      setLoading(false);
    }
  }, [
    filters.search,
    filters.status,
    filters.provider,
    filters.slaStatus,
    filters.riskLevel,
    filters.sortBy,
    filters.sortDir,
    filters.page,
    filters.pageSize,
  ]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { data, loading, error, refetch: fetch };
}
